import { logError } from '@/lib/sentry.lib';

import type { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import type { UndefinedOnPartialDeep } from 'type-fest';

import { useAlignProposals, useServicePrices, useTimeWindows } from '@repo/hooks';
import { isOrder } from '@repo/types/order';
import type { Service } from '@repo/types/service';
import type { TimeWindowResponseItem } from '@repo/types/time-windows';
import type { TimeWindowListItem } from '@repo/ui';
import { computeDayKey } from '@repo/utils';

import { getServiceEndpoint } from '@/api/helpers.api';
import { useAssetServices } from '@/hooks/asset-services.hooks';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getServiceTimeWindowStartUtc, getServiceTimeZone } from '@/utils/services.utils';
import { getAppConfig } from '@/utils/window.utils';

import {
    evaluateTimeWindowAlignProposal,
    getAffectedServiceUpdateItems,
    getDisplayedTimeWindowAlignProposalRequests,
} from './time-window-align-proposal.utils';
import {
    buildServicePriceRequests,
    buildServiceTimeWindowsPayload,
    getTimeWindowPriceState,
    isTimeWindowInvalidForUpdate,
} from './time-window-data.utils';
import { getDisplayedTimeWindows } from './time-window.utils';

interface UseTimeWindowModalDataParams {
    service: UndefinedOnPartialDeep<Service>;
    activeMonthStartDate: Dayjs;
}

interface UseTimeWindowCandidatesParams {
    service: UndefinedOnPartialDeep<Service>;
    activeMonthStartDate: Dayjs;
    selectedDayKey: string | null;
    selectedTimeWindowId: string | undefined;
}

const EMPTY_TIME_WINDOWS: TimeWindowResponseItem[] = [];

const useTimeWindowModalData = ({ service, activeMonthStartDate }: UseTimeWindowModalDataParams) => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const { data: baselineData } = useAsset(assetType, assetId, { includePendingChanges: false });
    const appConfig = getAppConfig();
    const servicesBasePath = getServiceEndpoint(appConfig.api.services.classicSalja, 'services');
    const services = useAssetServices(data);
    const baselineServices = useAssetServices(baselineData);
    const orderData = isOrder(data) ? data : undefined;
    const baselineOrderData = isOrder(baselineData) ? baselineData : undefined;
    const timeWindowsPayload = buildServiceTimeWindowsPayload({
        activeMonthStartDate,
        orderData,
        services,
        service,
    });

    const {
        data: timeWindowsResponse,
        isError,
        error,
        isFetching,
    } = useTimeWindows({
        servicesBasePath,
        payload: timeWindowsPayload,
    });

    useEffect(() => {
        if (isError) {
            logError(error);
        }
    }, [error, isError]);

    return {
        orderData,
        services,
        baselineOrderData,
        baselineServices,
        timeWindows: timeWindowsResponse?.timeWindows ?? EMPTY_TIME_WINDOWS,
        isFetching,
        isError,
    };
};

export const useTimeWindowCandidates = ({
    service,
    activeMonthStartDate,
    selectedDayKey,
    selectedTimeWindowId,
}: UseTimeWindowCandidatesParams) => {
    const serviceTimeWindowStartUtc = getServiceTimeWindowStartUtc(service);
    const { orderData, services, baselineOrderData, baselineServices, timeWindows, isFetching, isError } =
        useTimeWindowModalData({
            service,
            activeMonthStartDate,
        });
    const appConfig = getAppConfig();
    const servicesBasePath = getServiceEndpoint(appConfig.api.services.classicSalja, 'services');
    const serviceTimeZone = getServiceTimeZone(service);
    const currentTimeWindowId = service.timeWindow?.timeWindowId;
    const currentDeliveryDayKey = computeDayKey(serviceTimeWindowStartUtc.toISOString(), serviceTimeZone);
    const displayedTimeWindows = getDisplayedTimeWindows({
        timeWindows,
        selectedDayKey,
        timeZone: serviceTimeZone,
        serviceTimeWindow: service.timeWindow,
        currentDeliveryDayKey,
    });
    const alignProposalState = getDisplayedTimeWindowAlignProposalRequests({
        orderData,
        services,
        service,
        timeWindows: displayedTimeWindows,
    });
    const alignProposalQueries = useAlignProposals({
        servicesBasePath,
        requests: alignProposalState.requests,
    });
    const servicePriceRequests = buildServicePriceRequests({
        orderData,
        services,
        service,
        timeWindows: displayedTimeWindows,
    });
    const priceQueries = useServicePrices({ servicesBasePath, requests: servicePriceRequests });
    const {
        timeWindowPrices,
        currentServicePrice,
        hasServicePriceError,
        loadingTimeWindowIds: servicePriceLoadingTimeWindowIds,
    } = getTimeWindowPriceState({
        orderData,
        service,
        timeWindows: displayedTimeWindows,
        requests: servicePriceRequests,
        priceQueries,
    });
    const displayedTimeWindowPrices =
        currentTimeWindowId && currentServicePrice && !timeWindowPrices[currentTimeWindowId]
            ? { ...timeWindowPrices, [currentTimeWindowId]: currentServicePrice }
            : timeWindowPrices;
    const alignProposalBlockedTimeWindowIds = new Set(alignProposalState.blockedTimeWindowIds);
    const alignProposalLoadingTimeWindowIds = new Set<string>();
    const affectedServiceUpdateItemsByTimeWindowId: Record<
        string,
        ReturnType<typeof getAffectedServiceUpdateItems>
    > = {};
    const timeWindowErrorCodesById: Record<string, string> = {};

    for (const [index, request] of alignProposalState.requests.entries()) {
        const alignProposalQuery = alignProposalQueries[index];

        if (!alignProposalQuery || alignProposalQuery.isPending) {
            alignProposalLoadingTimeWindowIds.add(request.timeWindowId);
            alignProposalBlockedTimeWindowIds.add(request.timeWindowId);
            continue;
        }

        if (alignProposalQuery.isError) {
            alignProposalBlockedTimeWindowIds.add(request.timeWindowId);
            continue;
        }

        if (!orderData) {
            alignProposalBlockedTimeWindowIds.add(request.timeWindowId);
            continue;
        }

        const alignProposalEvaluation = evaluateTimeWindowAlignProposal({
            orderData,
            services,
            alignProposal: alignProposalQuery.data,
            requestedServiceLines: request.payload.serviceLines,
        });

        if (alignProposalEvaluation.isBlocking) {
            alignProposalBlockedTimeWindowIds.add(request.timeWindowId);
            if (alignProposalEvaluation.errorCode) {
                timeWindowErrorCodesById[request.timeWindowId] = alignProposalEvaluation.errorCode;
            }
            continue;
        }

        const affectedServiceUpdateItems = getAffectedServiceUpdateItems({
            affectedServices: alignProposalEvaluation.affectedServices,
            dateTimeFormat: appConfig.dateTimeFormat,
            timeFormat: appConfig.timeFormat,
        });

        if (affectedServiceUpdateItems.length > 0) {
            affectedServiceUpdateItemsByTimeWindowId[request.timeWindowId] = affectedServiceUpdateItems;
        }
    }

    const loadingTimeWindowIds = new Set([...alignProposalLoadingTimeWindowIds, ...servicePriceLoadingTimeWindowIds]);
    const timeWindowItems: TimeWindowListItem[] = displayedTimeWindows.flatMap((timeWindow) => {
        if (!timeWindow.timeWindowId) {
            return [];
        }

        return [
            {
                affectedServiceUpdateItems: affectedServiceUpdateItemsByTimeWindowId[timeWindow.timeWindowId] ?? [],
                errorCode: timeWindowErrorCodesById[timeWindow.timeWindowId],
                fromDateTimeUtc: timeWindow.fromDateTimeUtc,
                id: timeWindow.timeWindowId,
                isLoading: loadingTimeWindowIds.has(timeWindow.timeWindowId),
                priceLabel: displayedTimeWindowPrices[timeWindow.timeWindowId],
                toDateTimeUtc: timeWindow.toDateTimeUtc,
                tspName: timeWindow.tspName,
                timeZone: timeWindow.timeZone,
            },
        ];
    });
    const selectedTimeWindowHasErrorCode =
        selectedTimeWindowId !== undefined && timeWindowErrorCodesById[selectedTimeWindowId] !== undefined;
    const isSelectedTimeWindowInvalid =
        selectedTimeWindowId !== undefined &&
        selectedTimeWindowId !== currentTimeWindowId &&
        (selectedTimeWindowHasErrorCode ||
            isTimeWindowInvalidForUpdate({
                alignProposalBlockedTimeWindowIds,
                currentServicePrice,
                timeWindowId: selectedTimeWindowId,
                timeWindowPrices,
            }));

    return {
        orderData,
        services,
        baselineOrderData,
        baselineServices,
        timeWindows,
        isError,
        isFetching,
        hasServicePriceError,
        isSelectedTimeWindowInvalid,
        timeWindowItems,
    };
};
